import { useLayoutEffect, useState, type RefObject } from 'react';

import {
  USER_TABLE_V2_LABEL_CHIP_GAP_PX,
  USER_TABLE_V2_LABEL_OVERFLOW_CHIP_WIDTH_PX,
} from '../../../constants/userTableV2.js';

type Args = {
  totalChips: number;
  containerRef: RefObject<HTMLElement | null>;
  measureRef: RefObject<HTMLElement | null>;
};

// Initial count used before the browser has laid out the cell
const INITIAL_VISIBLE_COUNT = 3;

// Returns how many of `totalChips` fit inside `containerRef` based on the widths rendered inside `measureRef`
const useLabelChipFit = ({ totalChips, containerRef, measureRef }: Args) => {
  const [visibleCount, setVisibleCount] = useState(
    Math.min(INITIAL_VISIBLE_COUNT, totalChips),
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer) return;

    const recalc = () => {
      const containerWidth = container.clientWidth;

      if (containerWidth === 0) return;

      const chips = Array.from(measurer.children) as HTMLElement[];

      let used = 0;
      let count = 0;
      for (let i = 0; i < chips.length; i++) {
        const isLastChip = i === chips.length - 1;
        const reserveOverflow = isLastChip
          ? 0
          : USER_TABLE_V2_LABEL_OVERFLOW_CHIP_WIDTH_PX +
            USER_TABLE_V2_LABEL_CHIP_GAP_PX;
        const gap = i > 0 ? USER_TABLE_V2_LABEL_CHIP_GAP_PX : 0;
        const chipWidth = chips[i].offsetWidth;

        if (used + gap + chipWidth + reserveOverflow > containerWidth) break;

        used += gap + chipWidth;
        count = i + 1;
      }
      setVisibleCount(count);
    };

    recalc();
    const observer = new ResizeObserver(recalc);
    observer.observe(container);
    return () => observer.disconnect();
  }, [totalChips, containerRef, measureRef]);

  return visibleCount;
};

export default useLabelChipFit;
